/**
 * WhatsApp Business API Service
 * Handles sending messages via WhatsApp Business API
 */

const WHATSAPP_API_URL = "https://graph.facebook.com/v22.0";

/**
 * Format phone number to international format
 * Adds country code (91 for India) if not present
 * @param {string} phoneNumber - Phone number (10 digits)
 * @returns {string} - Formatted phone number with country code
 */
const formatPhoneNumber = (phoneNumber) => {
  // Remove any non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, "");

  // If it's a 10-digit number, assume it's Indian and add 91
  if (cleaned.length === 10) {
    return `91${cleaned}`;
  }

  // If it already has country code, return as is
  return cleaned;
};

/**
 * Send WhatsApp template message
 * @param {string} phoneNumber - Recipient phone number
 * @param {string} templateName - WhatsApp template name
 * @param {string} userName - User's name (optional, for personalization)
 * @returns {Promise<Object>} - API response
 */
export const sendWhatsAppMessage = async (
  phoneNumber,
  templateName = "hello_world",
  userName = ""
) => {
  try {
    const phoneId = process.env.REACT_APP_WHATSAPP_PHONE_ID;
    const accessToken = process.env.REACT_APP_WHATSAPP_ACCESS_TOKEN;

    // Validate configuration
    if (!phoneId || !accessToken) {
      throw new Error(
        "WhatsApp API credentials not configured. Please check .env.local file."
      );
    }

    if (accessToken === "YOUR_ACCESS_TOKEN_HERE") {
      throw new Error(
        "Please replace YOUR_ACCESS_TOKEN_HERE with your actual WhatsApp access token in .env.local"
      );
    }

    // Format phone number
    const formattedPhone = formatPhoneNumber(phoneNumber);

    // Prepare request payload
    const payload = {
      messaging_product: "whatsapp",
      to: formattedPhone,
      type: "template",
      template: {
        name: templateName,
        language: {
          code: "en_US",
        },
      },
    };

    // Make API request
    const response = await fetch(`${WHATSAPP_API_URL}/${phoneId}/messages`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || "Failed to send WhatsApp message");
    }

    console.log("WhatsApp message sent successfully:", data);
    return {
      success: true,
      messageId: data.messages?.[0]?.id,
      data,
    };
  } catch (error) {
    console.error("WhatsApp API Error:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Send welcome message to new user
 * @param {string} phoneNumber - User's phone number
 * @param {string} userName - User's name
 * @returns {Promise<Object>} - Result of message sending
 */
export const sendWelcomeMessage = async (phoneNumber, userName) => {
  const templateName =
    process.env.REACT_APP_WHATSAPP_TEMPLATE_NAME || "hello_world";
  return sendWhatsAppMessage(phoneNumber, templateName, userName);
};

const whatsappService = {
  sendWhatsAppMessage,
  sendWelcomeMessage,
  formatPhoneNumber,
};

export default whatsappService;
